import { CalendarIcon, CheckIcon, DatabaseIcon, PencilAltIcon, PencilIcon, PlusCircleIcon, TrashIcon, UploadIcon, XIcon } from "@heroicons/react/outline";
import { useCallback, useContext, useEffect, useState } from "react";
import { useModal, WorkflowContext } from "../contexts";
import WorkflowSelectModal from "./WorkflowSelectModal";
import { Document, SignatureField, Region } from "../interfaces";
import useUser from "../hooks/useUser";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import OAuthModal from "./OAuthModal";
import ErrorModal from "./ErrorModal";
import { getData, OAuthError, postData, putData } from "../backend";
import { BLOCK_ICON_MAP, RECIPIENT_COLOUR_MAP } from "../utils";
import { useTranslation } from "react-i18next";
import PDFViewer from "./PDFViewer";
import LoadingModal from "./LoadingModal";
import WorkflowDocumentOutputName from "./WorkflowDocumentOutptName";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import PayWallModalPro from "./PayWallModalPro";
import GooglePickerButton from "./GooglePickerButton";
import FileUploadModal from "./FileUploadModal";

function WorkflowDocumentPDFOverlay() {
  const { hasPermission } = useUser();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const { user } = useUser();

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentScale, setCurrentScale] = useState(1);

  const [regions, setRegions] = useState<Array<Region>>([])

  const [textColor, bgColor] = RECIPIENT_COLOUR_MAP[1];

  const isOpened = true;
  const [clickedField, setClickedField] = useState<SignatureField>();

  const [hasChanges, setHasChanges] = useState(false);

  const index = (() => {
    const hash = location.hash.split("#")[1];

    if (hash === "signable")
      return workflow.documents.indexOf(workflow.documents.filter(d => d.isSignable)[0]);

    return (parseInt(hash) || 1) - 1;
  })();

  const initialDocument = workflow.documents[index] ?? workflow.documents[0];
  const [document, setDocument] = useState<Document>();

  const documentIndex = workflow.documents.findIndex(d => d.id === document?.id) + 1;

  useEffect(() => {
    if (!document || pdfUrl)
      return;

    postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`)
      .then(({ url }) => setPdfUrl(url));

    setRegions(document.fieldRegions || []);
  }, [document, pdfUrl]);

  useEffect(() => {
    setDocument(initialDocument);
  }, [initialDocument]);

  async function addSignatureLinkSource() {
    if (!workflow || !mutateWorkflow)
      return;

    openModal(<LoadingModal title={t("Adding new Block")} subtitle={t("Please wait")} />)

    const sourceType = "SIGNATURE_LINK";
    try {
      const sourceOptions = { sourceType };

      await postData(`/workflows/${workflow!.id}/source/`, sourceOptions);
      await mutateWorkflow();

      closeModal();

      //@ts-ignore
      const dataLayer = window.dataLayer;
      dataLayer.push({ "event": "add_source", 'source_type': sourceType });

    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);
    }
  }

  function handleInsertSignatureField(type: SignatureField, recipient = 1) {
    setHasChanges(true);

    setClickedField(type)
    setTimeout(() => setClickedField(undefined), 500);

    const id = String(new Date().getTime());
    setRegions(prev => [...prev, { rect: { x: 100, y: 400, width: 200, height: 40, scale: currentScale }, id, type, recipient, pageNumber: currentPage }])
  }

  function updateRegions(regions: Array<Region>) {
    setHasChanges(true);
    setRegions(regions);
  }

  async function handleSaveChanges() {
    if (!document)
      return;

    const updatedDocument = { ...document, fieldRegions: regions };
    setDocument(updatedDocument);

    await putData(`/workflows/documents/${document!.id}/`, updatedDocument)
      .then(setDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);

    setHasChanges(false);
  }

  function toggleRemoveBranding(flag: boolean) {
    if (!user?.subscription?.featureRemoveBranding) {
      openModal(<PayWallModalPro />)
      return;
    }

    updateDocument("removeBranding", flag);
  }

  const uploadPDfFile = useCallback(async (content: Blob, filename?: string, isNewFile?: boolean) => {
    if (isNewFile)
      setPdfUrl(null);

    return postData(`/workflows/documents/${document!.id}/generate-pdf-upload-url/`)
      .then(({ url }) => {
        return fetch(url, {
          method: "PUT",
          headers: {
            "content-type": "application/pdf"
          },
          body: content
        })
      })
      .then(() => postData(`/workflows/documents/${document!.id}/confirm-pdf-upload-status/`, { filename }))
      .then(() => postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`))
      .then(({ url }) => setPdfUrl(url))
      .then(() => setDocument({ ...document!, file: { ...document!.file!, name: filename! } }))
      .then(() => getData(`/workflows/documents/${document!.id}/`))
      .then(setDocument)
      .catch(console.error);
  }, [document]);


  function handleUploadFile(file: File): Promise<any> {
    closeModal();

    const content = new Blob([file], { type: "application/pdf" });
    return uploadPDfFile(content, file.name, true);
  }

  async function handleChangeFile(file: any) {
    setPdfUrl(null); // NOTE: This will keep the viewer as "Loading..." if an error occurs from selecting the file.

    try {
      await putData(`/workflows/documents/${document!.id}/file/`, { file })
        .then(() => postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`))
        .then(({ url }) => setPdfUrl(url))
        .then(() => setDocument({ ...document!, file: { ...document!.file!, name: file.name } }))
    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />)
      else
        openModal(<ErrorModal details={error} />)
    }

    await mutateWorkflow();
  }

  async function updateDocument(field: string, value: any) {
    if (!document)
      return;

    const updatedDocument = { ...document, [field]: value };
    setDocument(updatedDocument);

    await putData(`/workflows/documents/${document!.id}/`, updatedDocument)
      .then(setDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);
  }

  if (!document) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full w-full">
        <div className="font-semibold font-gilroy text-gray-600 text-center w-96">
          {t("This workflow contains no document templates, would you like to add some?")}
        </div>
        <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Add Workflow Template")} subtitle={t("Add a template to your workflow")} showDocuments workflowContext={workflowContext} />)}>
          {t("Add Document Template")}
        </button>
      </div>
    );
  }

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/document")}>
      {hasPermission(workflow.team, "workflows.edit") &&
        <TransitionWorkflowPageSideBar>
          {!workflow.source && <>
            <div className="flex flex-col gap-4 justify-center h-full">
              <div className="flex flex-col gap-2 font-semibold text-gray-600">
                <div className="flex items-center">
                  <DatabaseIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Connect a Source")}
                  </div>
                </div>
                {t("Automatically create documents from data in HubSpot, Google Sheets and more")}
                <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Source")} subtitle={t("Create or select a source where your data will come from")} showSources workflowContext={workflowContext} />)}>
                  {t("Connect")}
                </button>
                <div className="flex items-center gap-2 my-4">
                  <div className="h-px bg-gray-600 w-full" />
                  <div className="flex-shrink-0">or</div>
                  <div className="h-px bg-gray-600 w-full" />
                </div>
                <div className="flex items-center">
                  <PencilAltIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Create a Signature Link")}
                  </div>
                </div>
                {t("Add signable fields to your document and generate a link for signing")}
                <button className="btn btn-purple" onClick={addSignatureLinkSource}>
                  {t("Configure")}
                </button>
              </div>
            </div>
          </>}

          {workflow.source &&
            <>
              <button className="btn btn-white" onClick={() => history.push(url + "/document?edit" + ((documentIndex > 1) ? `#${documentIndex}` : ""))}>
                Edit PDF Fillable Fields
              </button>

              <WorkflowDocumentOutputName initialValue={document.outputName} onEnter={(value) => updateDocument("outputName", value)} />

              <div className="flex justify-start items-center font-gilroy font-semibold text-sm  w-full">
                <ToggleButton value={!!user?.subscription?.featureRemoveBranding && document.removeBranding} onChange={toggleRemoveBranding} />
                <div className={`font-semibold text-sm ml-2 mr-1 ${true ? "text-gray-600" : "text-gray-400"}`}>
                  {t("Remove Portant PDF branding")}
                </div>
                <ProLabel />
              </div>
              <hr />
            </>
          }

          {document.isSignable &&
            <>
              <div className="border border-gray-400 rounded">
                <div className={`flex flex-col gap-1 border-b px-2 pt-1 bg-opacity-10 rounded ${bgColor}`}>
                  <div className={`flex items-center w-full ${textColor} font-gilroy font-semibold text-sm`}>
                    {t("Signature Fields")}
                  </div>
                </div>

                <div className={`flex flex-col ${document && isOpened ? "block" : "hidden"}`}>
                  <div className="flex items-center text-sm border-b px-2 py-1">
                    <PencilAltIcon className="w-4 h-4 mr-2" />
                    <div className="font-semibold whitespace-nowrap py-2">
                      {t("Signature")}
                    </div>
                    <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "SIGNATURE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("SIGNATURE")}>
                      {clickedField === "SIGNATURE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                    </button>
                  </div>
                  <div className="flex items-center text-sm border-b px-2 py-1">
                    <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
                      AB
                    </div>
                    <div className="font-semibold whitespace-nowrap py-2">
                      {t("Initials")}
                    </div>
                    <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "INITIALS" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("INITIALS")}>
                      {clickedField === "INITIALS" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                    </button>
                  </div>
                  <div className="flex items-center text-sm border-b px-2 py-1">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <div className="font-semibold whitespace-nowrap py-2">
                      {t("Date")}
                    </div>
                    <div className="text-xs ml-auto flex items-start group cursor-pointer hover:text-blue hover:underline">
                      <div className="ml-1">
                        {(workflow.owner?.timeFormat) === "HOUR_12" && "12Hr"}
                        {(workflow.owner?.timeFormat) === "HOUR_24" && "24Hr"}
                      </div>
                      <PencilIcon className="opacity-0 group-hover:opacity-100 w-3 h-3 ml-1" />
                    </div>
                    <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "DATE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("DATE")}>
                      {clickedField === "DATE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                    </button>
                  </div>
                  <div className="flex items-center text-sm border-b px-2 py-1">
                    <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
                      Tt
                    </div>
                    <div className="font-semibold whitespace-nowrap py-2">
                      {t("Text")}
                    </div>
                    <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("TEXT")}>
                      {clickedField === "TEXT" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                    </button>
                  </div>

                  <div className="flex items-center text-sm px-2 py-1">
                    <CheckIcon className="w-4 h-4 mr-2 border border-black rounded" />
                    <div className="font-semibold whitespace-nowrap py-2">
                      {t("Checkbox")}
                    </div>
                    <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("CHECKBOX")}>
                      {clickedField === "CHECKBOX" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                    </button>
                  </div>
                  <div className="flex flex-col py-2 px-2 border-t gap-2 text-sm">
                    <div className=" text-gray-600">
                      {t("Signable fields will be inserted into the document on page: ")}<b>{currentPage}</b>
                    </div>
                  </div>
                </div>
              </div>
              <button className={`btn ${hasChanges ? "btn-blue" : "btn-white"}`} onClick={handleSaveChanges}>
                {t("Save Changes")}
              </button>
            </>
          }
        </TransitionWorkflowPageSideBar>
      }

      <TransitionWorkflowPageContent>
        <div className="flex w-full items-center pb-1 border-b">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP[document.documentType]} alt="Google File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(document.file?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {document.file?.name}
                </div>
              </div>
              {hasPermission(workflow.team, "workflows.edit") &&
                <div className="flex gap-2 w-full">
                  <div className="text-gray-600 text-sm font-gilroy font-medium">
                    {t("Change file:")}
                  </div>
                  <GooglePickerButton className="flex items-center text-gray-400 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 group" blockType={"PDF"} onPicked={handleChangeFile}>
                    <img className="h-4 mr-1 grayscale group-hover:grayscale-0" src={BLOCK_ICON_MAP["GOOGLE_DRIVE"]} alt="Google Drive" /><>{t("Select from Google Drive")}</>
                  </GooglePickerButton>
                  <button className="flex items-center text-gray-400 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 group" onClick={() => openModal(<FileUploadModal onUpload={handleUploadFile} allowedFormats={['PDF']} />)}>
                    <UploadIcon className="h-4 mr-1 group-hover:text-blue" /><>Upload from device</>
                  </button>
                  {false && workflow.documents.length > 1 &&
                    <div className="flex items-center text-gray-500 text-sm font-gilroy font-medium cursor-pointer hover:underline hover:text-red" onClick={() => alert(t("Not implemented"))}>
                      <TrashIcon className="h-4 mr-1" /><>{t("Remove block")}</>
                    </div>
                  }
                </div>
              }
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
          </div>
        </div>
        {pdfUrl
          ? <PDFViewer pdfUrl={pdfUrl} setCurrentPage={setCurrentPage} setCurrentScale={setCurrentScale} regions={regions} updateRegions={updateRegions} />
          : <div className="w-full h-[800px] flex items-center justify-center text-gray-600">{t("Loading...")}</div>
        }
      </TransitionWorkflowPageContent>
    </TransitionWorkflowPage>
  );

}

export default WorkflowDocumentPDFOverlay;
